import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

const CategoryFilter = ({
  categories,
  filteredCategoryIds,
  onFilterChange,
}) => {
  const [localFiltered, setLocalFiltered] = useState(filteredCategoryIds);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setLocalFiltered(filteredCategoryIds);
  }, [filteredCategoryIds]);

  const handleToggle = (categoryId) => {
    const newFiltered = localFiltered.includes(categoryId)
      ? localFiltered.filter((id) => id !== categoryId)
      : [...localFiltered, categoryId];

    setLocalFiltered(newFiltered);
    onFilterChange(newFiltered);
  };

  const handleSelectAll = () => {
    const allIds = categories.map((cat) => cat.id);
    setLocalFiltered(allIds);
    onFilterChange(allIds);
  };

  const handleDeselectAll = () => {
    setLocalFiltered([]);
    onFilterChange([]);
  };

  // Group categories by type
  const groupedCategories = categories.reduce((acc, category) => {
    const type = category.type || "OTHER";
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(category);
    return acc;
  }, {});

  const typeOrder = ["INCOME", "EXPENSE", "INVESTMENT", "OTHER"];
  const sortedTypes = typeOrder.filter((type) => groupedCategories[type]);

  const getFilterSummary = () => {
    const totalCategories = categories.length;
    const selectedCount = localFiltered.length;

    if (selectedCount === 0) {
      return "No categories selected";
    }
    if (selectedCount === totalCategories) {
      return "All categories selected";
    }
    return `${selectedCount} of ${totalCategories} categories selected`;
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ mb: 2 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: "action.hover",
          "&:hover": { backgroundColor: "action.selected" },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
        >
          <FilterListIcon />
          <Typography variant="h6">Category Filter</Typography>
          <Chip
            label={getFilterSummary()}
            size="small"
            sx={{ ml: "auto", mr: 2 }}
            color={localFiltered.length === 0 ? "error" : "primary"}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button size="small" variant="outlined" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button size="small" variant="outlined" onClick={handleDeselectAll}>
              Deselect All
            </Button>
          </Box>

          {sortedTypes.map((type) => (
            <Box key={type} sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                  color: "primary.main",
                  textTransform: "capitalize",
                }}
              >
                {type.toLowerCase()}
              </Typography>
              <FormGroup>
                {groupedCategories[type].map((category) => (
                  <FormControlLabel
                    key={category.id}
                    control={
                      <Checkbox
                        checked={localFiltered.includes(category.id)}
                        onChange={() => handleToggle(category.id)}
                        size="small"
                      />
                    }
                    label={category.display_name || category.name}
                    sx={{ ml: 1 }}
                  />
                ))}
              </FormGroup>
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default CategoryFilter;
